import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import { StandardChartView } from '../../../core/MainStandardChartView';
import DEFINE from '../../../util/DEFINE';

/**
 * 根据 children 中的 name 属性对数据进行分组
 * @param {Array} data - 原始数据数组，每个元素包含 children 数组
 * @returns {Array} - 分组后的数据数组，每个元素包含 name 和对应的 children 数组
 */
/**
 * 根据 children 中的 name 属性对数据进行分组
 * @param data - 原始数据数组，每个元素包含 children 数组
 * @returns 分组后的数据数组，每个元素包含 name 和对应的 children 数组
 */
function groupDataByChildrenName(data: any[]): any[] {
  // 用于存储分组后的数据
  const groupedChildren: { [key: string]: { name: string; value: number }[] } = {};

  // 遍历原始数据中的每一项
  data.forEach(item => {
    // 遍历当前项的 children 数组
    item.children.forEach(child => {
      // 如果 groupedChildren 中还没有该 name 的数组，则创建一个空数组
      if (!groupedChildren[child.name]) {
        groupedChildren[child.name] = [];
      }
      // 将当前 child 添加到对应 name 的数组中
      groupedChildren[child.name].push(child);
    });
  });

  // 将 groupedChildren 转换为数组格式，每个元素是一个对象，包含 name 和对应的 children 数组
  return Object.entries(groupedChildren).map(([name, children]) => ({
    name,
    children
  })) as { name: string; children: { name: string; value: number }[] }[];
}

const visInfo: Layer['visInfo'] = {
  id: 'barDistribution',
  name_en: 'barDistribution',
  name_zh: '柱状分布图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    data: amount => amount > 0 && amount <= 10,
    x: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    }
  }
};

/**
 * ECharts 的配置项
 * @see https://echarts.apache.org/zh/option.html#series-line
 */
type EChartsPieSeries = {
  roseType?: unknown;
  radius?: unknown;
};

export interface Spec extends StandardChartLayerSpec, EChartsPieSeries {
  encoding: {
    x: string;
    y: string;
  };
}

export const barDistribution = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec) {
    const { data: sourceData } = api;
    const {
      dataIndex = 0,
      encoding: { x, y }
    } = spec;
    
    const bgData = sourceData[0].values.map(item => ({ item, value: item[y] }));
    const groupData = groupDataByChildrenName(sourceData[0].values);
    const sereisOption = groupData.map(item => {
      return {
        data: item.children,
        type: 'bar',
        name: item.name,
        barMaxWidth: '12.85%',
        itemStyle: {
          color: '#4D5999'
        },
        barGap: '50%',
        z: 4
      };
    });
    const xAxis = sourceData[0].values.map(item => item[x]);
    // * -------------------------- chart
    return {
      tooltip: {
        show: true,
        confine: true
      },
      xAxis: [
        {
          type: 'category',
          show: true,
          data: xAxis
        },
        {
          type: 'category',
          show: false
        }
      ],
      yAxis: [
        {
          __dataIndex: dataIndex,
          __encodeField: [y],
          type: 'value',
          show: true
        }
      ],
      legend: {
        data: []
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: '90%',
          barMinWidth: '90%',
          xAxisIndex: 1,
          data: bgData,
          label: {
            show: true,
            position: 'top',
            color: '#292933'
          },
          itemStyle: {
            color: 'rgba(77,89,153,0.20)'
          },
          tooltip: {
            show: true
          }
        },
        ...sereisOption
      ]
    };
  }
});
